import React, { useState } from 'react';
import {
  DEFAULT_CEILING_STYLE,
  PATTERNED_CEILING_TYPES,
  ACCENT_CEILING_TYPES,
  PBR_CEILING_MATERIAL_TO_SET,
} from './Ceiling3D.jsx';
import './ceilingStyleBox.scss';

// سقف دو انتخابِ مستقل دارد (نگاه کنید به Ceiling3D.jsx):
//   «مدل» = شکل و تزئینات (نورِ مخفی، چندسطحی، گچ‌بری، ...)
//   «جنس» = سطحِ سقف (رنگ، گچِ واقعی، چوبِ واقعی، ...)
// هر مدلی با هر جنسی ترکیب می‌شود
const CEILING_MODEL_OPTIONS = [
  { value: 'flat', label: 'ساده' },
  { value: 'tile', label: 'کاذب/کناف' },
  { value: 'cove', label: 'نورِ مخفی' },
  { value: 'twoTone', label: 'چندسطحی' },
  { value: 'multiCove', label: 'چندسطحی + نورِ مخفی' },
  { value: 'plasterFrame', label: 'گچ‌بری' },
];

const CEILING_MATERIAL_OPTIONS = [
  { value: 'paint', label: 'رنگ' },
  { value: 'glossy', label: 'کشسان/براق' },
  { value: 'plasterGray', label: 'گچِ خاکستری ✦' },
  { value: 'plasterWhite', label: 'گچِ بافت‌دارِ سفید ✦' },
  { value: 'woodPine', label: 'چوبِ روشن ✦' },
  { value: 'woodDark', label: 'چوبِ تیره ✦' },
];

const MODEL_HINTS = {
  flat: 'فقط خودِ سطحِ سقف، بدونِ تزئین — رایج‌ترین حالت.',
  tile: 'شبکه‌ی درزِ پنل‌های مربعی روی سطح — شبیهِ سقف‌های کاذبِ آلومینیومی/کنافِ مشبک. اندازه‌ی پنل در تبِ «الگو».',
  cove: 'یک نوارِ نورانی و شیارِ تیره نزدیکِ دیوارها، با درخششِ نرم روی سقف — جلوه‌ی نورِ مخفی/غیرمستقیم.',
  twoTone: 'یک حاشیه‌ی پایین‌تر (واقعاً چند سانت پایین‌تر) دورتادورِ سقف — سقفِ کاذبِ دوسطحی.',
  plasterFrame: 'دو قابِ برجسته‌ی نازک نزدیکِ لبه‌ها — گچ‌بریِ دورتادورِ سقف.',
  multiCove:
    'دو پله‌ی واقعی (کاذبِ کناف) دورتادورِ سقف، و در هر پله یک نوارِ نورِ مخفی که خودش دیده می‌شود و روی سطحِ بالاتر می‌تابد. رنگِ نور از تبِ «جنس و رنگ».',
};

const MATERIAL_HINTS = {
  paint: 'سقفِ گچیِ رنگ‌شده، با بافتِ ملایمِ واقعیِ رنگ — هر رنگی که انتخاب کنی.',
  glossy: 'یک‌دست با سطحِ صیقلی/براق — سقفِ کشسانِ PVC.',
  plasterGray: 'عکسِ واقعیِ گچ/بتنِ خاکستریِ یک‌دست، با برجستگی/زبریِ واقعی.',
  plasterWhite: 'عکسِ واقعیِ گچِ سفیدِ مات با برجستگیِ ماله‌کشی — همان گچِ بافت‌دارِ دیوار.',
  woodPine: 'عکسِ واقعیِ چوبِ کاجِ روشن — برای سقف‌های چوبی.',
  woodDark: 'عکسِ واقعیِ چوبِ تیره‌ی گردویی — برای سقف‌های چوبیِ تیره.',
};

const MAIN_COLOR_PRESETS = ['#f2efe6', '#ffffff', '#eee7d8', '#d9d9d9', '#c9a876', '#e3d5c5'];
// یک تُنِ گرمِ زرد/کهربایی (#ffdf9b) عمداً اضافه شده — رایج‌ترین رنگِ واقعیِ نوارِ نورِ مخفی
const ACCENT_COLOR_PRESETS = ['#d9d3c4', '#ffffff', '#c9bba0', '#8a8a8a', '#5c4433', '#ffdf9b'];

const TABS = [
  { id: 'type', label: 'مدل' },
  { id: 'material', label: 'جنس و رنگ' },
  { id: 'pattern', label: 'الگو' },
  { id: 'light', label: 'نور' },
];

const LIGHT_MIN_PCT = 10;
const LIGHT_MAX_PCT = 100;

const TILE_MIN_CM = 30;
const TILE_MAX_CM = 100;

// برچسبِ رنگِ فرعی، چون معنایش بسته به نوعِ سقف فرق می‌کند
function accentLabel(type) {
  if (type === 'tile') return 'رنگِ درزِ پنل';
  if (type === 'cove' || type === 'multiCove') return 'رنگِ نورِ مخفی';
  if (type === 'twoTone') return 'رنگِ حاشیه‌ی پایین‌تر';
  if (type === 'plasterFrame') return 'رنگِ خطوطِ گچ‌بری';
  return 'رنگِ فرعی';
}

function ColorField({ label, value, presets, onChange }) {
  return (
    <div className="ceiling-style-box__field">
      {label && <span className="ceiling-style-box__label">{label}</span>}
      <div className="ceiling-style-box__swatches">
        {presets.map((c) => (
          <button
            key={c}
            type="button"
            className={`ceiling-style-box__swatch ${value === c ? 'is-active' : ''}`}
            style={{ background: c }}
            onClick={() => onChange(c)}
          />
        ))}
        <input
          type="color"
          className="ceiling-style-box__custom-color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

// پنلِ ادیتِ سقفِ یک اتاق در حالت سه‌بعدی — سه تب: مدل، جنس و رنگ، الگو.
// فقط از دیدِ اول‌شخص نمایش داده می‌شود (تصمیم در Create3D.jsx گرفته می‌شود، با لمسِ نشانه‌ی
// «ویرایشِ سقف» وقتی کاربر به سقف نگاه می‌کند)
function CeilingStyleBox({
  style,
  onChangeType,
  onChangeMaterial,
  onChangeColor,
  onChangeAccent,
  onChangeSize,
  onChangeLight,
  effectiveLight,
  onClose,
}) {
  const [activeTab, setActiveTab] = useState('type');
  const s = style || DEFAULT_CEILING_STYLE;
  const tileCm = s.tileCm || 60;
  const hasPattern = PATTERNED_CEILING_TYPES.includes(s.type);
  const hasAccent = ACCENT_CEILING_TYPES.includes(s.type);
  const material = s.material || 'paint';
  const lightPct = s.lightPct ?? 70;
  // «رنگ» هم از یک بافتِ واقعی استفاده می‌کند ولی خودش سفید/خنثی است، پس برچسبِ «روی عکس» لازم ندارد
  const isPhoto = material !== 'paint' && Boolean(PBR_CEILING_MATERIAL_TO_SET[material]);
  const modelLabel = CEILING_MODEL_OPTIONS.find((m) => m.value === s.type)?.label || '';
  const materialLabel = CEILING_MATERIAL_OPTIONS.find((m) => m.value === material)?.label || '';

  return (
    <div className="ceiling-style-box">
      <div className="ceiling-style-box__header">
        <span>تنظیماتِ سقفِ این اتاق</span>
        <button type="button" onClick={onClose}>
          بستن
        </button>
      </div>

      <div className="ceiling-style-box__tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? 'is-active' : ''}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'type' && (
        <div className="ceiling-style-box__panel">
          <div className="ceiling-style-box__pills ceiling-style-box__pills--wrap">
            {CEILING_MODEL_OPTIONS.map((t) => (
              <button
                key={t.value}
                type="button"
                className={s.type === t.value ? 'is-active' : ''}
                onClick={() => onChangeType(t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="ceiling-style-box__hint">{MODEL_HINTS[s.type] || MODEL_HINTS.flat}</p>
          <p className="ceiling-style-box__hint">
            الان: مدلِ «{modelLabel}» با جنسِ «{materialLabel}». جنس را از تبِ «جنس و رنگ» عوض کن — هر مدلی
            با هر جنسی ترکیب می‌شود. این سقف فقط روی همین اتاق اعمال می‌شود.
          </p>
        </div>
      )}

      {activeTab === 'material' && (
        <div className="ceiling-style-box__panel">
          <div className="ceiling-style-box__field">
            <span className="ceiling-style-box__label">جنسِ سقف</span>
            <div className="ceiling-style-box__pills ceiling-style-box__pills--wrap">
              {CEILING_MATERIAL_OPTIONS.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  className={material === m.value ? 'is-active' : ''}
                  onClick={() => onChangeMaterial(m.value)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          <p className="ceiling-style-box__hint">{MATERIAL_HINTS[material]}</p>
          <ColorField
            label={isPhoto ? 'رنگ اصلی (روی عکسِ واقعی اعمال می‌شود)' : 'رنگ اصلی'}
            value={s.color || '#f2efe6'}
            presets={MAIN_COLOR_PRESETS}
            onChange={onChangeColor}
          />
          {isPhoto && (
            <p className="ceiling-style-box__hint">
              جزئیات از خودِ عکس می‌آید. سفید = رنگِ طبیعیِ عکس؛ رنگِ تیره‌تر = تیره‌تر/رنگی‌تر.
            </p>
          )}
          {hasAccent && (
            <ColorField label={accentLabel(s.type)} value={s.accent || '#d9d3c4'} presets={ACCENT_COLOR_PRESETS} onChange={onChangeAccent} />
          )}
        </div>
      )}

      {activeTab === 'pattern' && (
        <div className="ceiling-style-box__panel">
          {hasPattern ? (
            <>
              <div className="ceiling-style-box__field">
                <span className="ceiling-style-box__label">اندازه‌ی الگو: {tileCm} سانتی‌متر</span>
                <input
                  type="range"
                  min={TILE_MIN_CM}
                  max={TILE_MAX_CM}
                  step={5}
                  value={tileCm}
                  onChange={(e) => onChangeSize({ tileCm: Number(e.target.value) })}
                  className="ceiling-style-box__slider"
                />
              </div>
              <p className="ceiling-style-box__hint">
                اندازه‌ی هر پنلِ سقفِ کاذب.
              </p>
            </>
          ) : (
            <p className="ceiling-style-box__hint">فقط مدلِ «کاذب/کناف» اندازه‌ی الگو دارد. اندازه‌ی بافتِ جنس‌های واقعی خودکار و متناسب با ابعادِ واقعی است.</p>
          )}
        </div>
      )}

      {activeTab === 'light' && (
        <div className="ceiling-style-box__panel">
          <div className="ceiling-style-box__field">
            <span className="ceiling-style-box__label">شدتِ روشناییِ کلِ اتاق: {Math.round(lightPct)}٪</span>
            <input
              type="range"
              min={LIGHT_MIN_PCT}
              max={LIGHT_MAX_PCT}
              step={5}
              value={lightPct}
              onChange={(e) => onChangeLight(Number(e.target.value))}
              className="ceiling-style-box__slider"
            />
          </div>
          {effectiveLight && effectiveLight.effective !== effectiveLight.base && (
            <p className="ceiling-style-box__hint">
              الان به‌خاطرِ{' '}
              {[
                effectiveLight.openCount ? `${effectiveLight.openCount} درِ باز/بازشو` : null,
                effectiveLight.openWindowCount ? `${effectiveLight.openWindowCount} پنجره‌ی باز` : null,
              ]
                .filter(Boolean)
                .join(' و ')}
              ، روشنایی {Math.round(effectiveLight.effective)}٪ است؛ با بسته‌شدنشان به {Math.round(effectiveLight.base)}٪
              برمی‌گردد.
            </p>
          )}
          <p className="ceiling-style-box__hint">
            ۷۰٪ = نورِ عادی. با باز شدنِ هر در:{' '}
            {effectiveLight?.hasWindow
              ? 'این اتاق پنجره دارد، پس ۱۰٪ روشن‌تر می‌شود (اگر ۹۰٪ یا بیشتر باشد، مستقیم ۱۰۰٪).'
              : 'این اتاق پنجره ندارد، پس ۲۵٪ روشن‌تر می‌شود (حداکثر ۱۰۰٪).'}{' '}
            بازشوها مثلِ درِ همیشه‌باز حساب می‌شوند. هر پنجره‌ی باز هم به نسبتِ اندازه‌اش روشن‌تر می‌کند (پنجره‌ای
            هم‌اندازه‌ی یک در ۹۰×۲۱۰ = ۱۰٪).
          </p>
        </div>
      )}
    </div>
  );
}

export default CeilingStyleBox;